import { MessageDescriptor, i18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import mapValues from "lodash/mapValues";
import { useMemo } from "react";

import {
  LANGUAGE_LOCALSTORAGE_KEY,
  LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY,
  LANGUAGE_MANUAL_OVERRIDE_KEY,
} from "config/localStorage";

// uses BCP-47 codes from https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
export const locales = {
  en: "English",
  es: "Spanish",
  zh: "Chinese",
  ko: "Korean",
  ru: "Russian",
  ja: "Japanese",
  fr: "French",
  de: "German",
} as const;

export type Locale = keyof typeof locales;
export type LocaleSource = "manual" | "browser" | "ip";
export type LocaleResolution = { locale: Locale; source: LocaleSource };

export const defaultLocale = "en";

const intlLocaleMap: Record<Locale, string> = {
  en: "en-US",
  es: "es",
  zh: "zh-CN",
  ko: "ko-KR",
  ru: "ru-RU",
  ja: "ja-JP",
  fr: "fr-FR",
  de: "de-DE",
};

const COUNTRY_LOCALE_MAP: Record<string, Locale> = {
  AR: "es", BO: "es", CL: "es", CO: "es", CR: "es", CU: "es", DO: "es", EC: "es", ES: "es", GQ: "es",
  GT: "es", HN: "es", MX: "es", NI: "es", PA: "es", PE: "es", PR: "es", PY: "es", SV: "es", UY: "es", VE: "es",
  CN: "zh", HK: "zh", MO: "zh", SG: "zh", TW: "zh",
  KR: "ko",
  RU: "ru", BY: "ru", KG: "ru",
  JP: "ja",
  BF: "fr", BI: "fr", BJ: "fr", CA: "fr", CD: "fr", CF: "fr", CG: "fr", CI: "fr", CM: "fr", DJ: "fr",
  FR: "fr", GA: "fr", GF: "fr", GN: "fr", GP: "fr", HT: "fr", KM: "fr", MC: "fr",
  MF: "fr", MG: "fr", ML: "fr", MQ: "fr", NC: "fr", NE: "fr", PF: "fr", PM: "fr", RE: "fr", RW: "fr", SC: "fr",
  SN: "fr", TD: "fr", TG: "fr", VU: "fr", WF: "fr", YT: "fr",
  AT: "de", BE: "de", CH: "de", DE: "de", LI: "de", LU: "de",
};

let automaticLocale: Locale = defaultLocale;

type LocaleCatalogModule = {
  messages?: Record<string, unknown>;
  default?: { messages?: Record<string, unknown> } | Record<string, unknown>;
};

const catalogLoaders: Record<Locale, () => Promise<LocaleCatalogModule>> = {
  en: () => import("../locales/en/messages.mjs"),
  es: () => import("../locales/es/messages.mjs"),
  zh: () => import("../locales/zh/messages.mjs"),
  ko: () => import("../locales/ko/messages.mjs"),
  ru: () => import("../locales/ru/messages.mjs"),
  ja: () => import("../locales/ja/messages.mjs"),
  fr: () => import("../locales/fr/messages.mjs"),
  de: () => import("../locales/de/messages.mjs"),
};

const loadedCatalogs = new Map<Locale, Record<string, unknown>>();
let latestLocaleActivation = 0;

async function loadLocaleCatalog(locale: Locale): Promise<Record<string, unknown>> {
  const cachedCatalog = loadedCatalogs.get(locale);
  if (cachedCatalog) {
    return cachedCatalog;
  }

  const module = await catalogLoaders[locale]();
  const messages: unknown = module.messages ?? module.default?.messages ?? module.default ?? module;

  if (!messages || typeof messages !== "object") {
    throw new Error(`Locale catalog "${locale}" does not export messages.`);
  }

  const catalog = messages as Record<string, unknown>;
  loadedCatalogs.set(locale, catalog);
  return catalog;
}

export function isSupportedLocale(locale: unknown): locale is Locale {
  return typeof locale === "string" && locale in locales;
}

export function getBrowserLocale(browserLanguages: readonly string[] = []): Locale {
  for (const language of browserLanguages) {
    const normalized = language.toLowerCase().split("-")[0];
    if (isSupportedLocale(normalized)) {
      return normalized;
    }
  }

  return defaultLocale;
}

export function getCountryLocale(countryCode: string | null | undefined): Locale {
  return COUNTRY_LOCALE_MAP[String(countryCode ?? "").trim().toUpperCase()] ?? defaultLocale;
}

export function getIntlLocale(locale = i18n.locale): string {
  return isSupportedLocale(locale) ? intlLocaleMap[locale] : intlLocaleMap[defaultLocale];
}

export function getTradingViewLocale(locale: string): Locale {
  return isSupportedLocale(locale) ? locale : defaultLocale;
}

export function resolveLocale({
  manualLocale,
  browserLanguages = [],
  countryCode,
}: {
  manualLocale?: string | null;
  browserLanguages?: readonly string[];
  countryCode?: string | null;
}): LocaleResolution {
  if (isSupportedLocale(manualLocale)) {
    return { locale: manualLocale, source: "manual" };
  }

  if (countryCode !== undefined && countryCode !== null) {
    return { locale: getCountryLocale(countryCode), source: "ip" };
  }

  return { locale: getBrowserLocale(browserLanguages), source: "browser" };
}

function getStorageValue(key: string): string | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function setStorageValue(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be disabled by browser privacy settings. Locale selection still works for this session.
  }
}

function restoreStorageValue(key: string, value: string | null) {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (_error) {
    // Storage can be disabled by browser privacy settings.
  }
}

export function getManualLocale(): Locale | undefined {
  const override = getStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY);
  const isConfirmed = getStorageValue(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY) === "true";
  if (isSupportedLocale(override) && (override !== defaultLocale || isConfirmed)) {
    return override;
  }

  const legacyLocale = getStorageValue(LANGUAGE_LOCALSTORAGE_KEY);
  if (isSupportedLocale(legacyLocale) && legacyLocale !== defaultLocale) {
    setStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY, legacyLocale);
    return legacyLocale;
  }

  return undefined;
}

export function getAutomaticLocale(): Locale {
  return automaticLocale;
}

export function setAutomaticLocale(locale: string): Locale {
  automaticLocale = isSupportedLocale(locale) ? locale : defaultLocale;
  return automaticLocale;
}

export function clearManualLocale() {
  try {
    localStorage.removeItem(LANGUAGE_MANUAL_OVERRIDE_KEY);
    localStorage.removeItem(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY);
    localStorage.removeItem(LANGUAGE_LOCALSTORAGE_KEY);
  } catch (_error) {
    // Keep the session locale even if storage is unavailable.
  }
}

export async function activateLocale(locale: string, { persistManualPreference = false } = {}): Promise<Locale> {
  const resolvedLocale = isSupportedLocale(locale) ? locale : defaultLocale;
  const activationId = ++latestLocaleActivation;
  const messages = await loadLocaleCatalog(resolvedLocale);

  // A newer locale request was started while this catalog was loading. Do not let an older request overwrite it.
  if (activationId !== latestLocaleActivation) {
    return isSupportedLocale(i18n.locale) ? i18n.locale : defaultLocale;
  }

  if (persistManualPreference) {
    setStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY, resolvedLocale);
    setStorageValue(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY, "true");
    // Keep the legacy key in sync for users upgrading from previous releases.
    setStorageValue(LANGUAGE_LOCALSTORAGE_KEY, resolvedLocale);
  }
  i18n.load(resolvedLocale, messages);
  i18n.activate(resolvedLocale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = resolvedLocale;
  }

  return resolvedLocale;
}

export async function dynamicActivate(locale: string) {
  const resolvedLocale = isSupportedLocale(locale) ? locale : defaultLocale;

  const previousOverride = getStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY);
  const previousConfirmedOverride = getStorageValue(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY);
  const previousLegacyLocale = getStorageValue(LANGUAGE_LOCALSTORAGE_KEY);

  // Persist intent before loading the catalog so a concurrent IP refinement cannot override an explicit choice.
  setStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY, resolvedLocale);
  setStorageValue(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY, "true");
  setStorageValue(LANGUAGE_LOCALSTORAGE_KEY, resolvedLocale);

  try {
    return await activateLocale(resolvedLocale);
  } catch (error) {
    restoreStorageValue(LANGUAGE_MANUAL_OVERRIDE_KEY, previousOverride);
    restoreStorageValue(LANGUAGE_MANUAL_OVERRIDE_CONFIRMED_KEY, previousConfirmedOverride);
    restoreStorageValue(LANGUAGE_LOCALSTORAGE_KEY, previousLegacyLocale);
    throw error;
  }
}

export async function activateAutomaticLocale(locale: string) {
  return activateLocale(setAutomaticLocale(locale));
}

export async function resetToAutomaticLocale() {
  clearManualLocale();
  return activateLocale(automaticLocale);
}

function getNavigatorLanguages(): readonly string[] {
  if (typeof navigator === "undefined") {
    return [];
  }

  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

export async function bootstrapI18n(
  browserLanguages = getNavigatorLanguages(),
  countryCode?: string | null
): Promise<LocaleResolution> {
  const automaticResolution = resolveLocale({ browserLanguages, countryCode });
  setAutomaticLocale(automaticResolution.locale);

  const resolution = resolveLocale({ manualLocale: getManualLocale(), browserLanguages, countryCode });
  await activateLocale(resolution.locale);
  return resolution;
}

export async function applyIpLocale(countryCode: string | null | undefined): Promise<LocaleResolution> {
  const manualLocale = getManualLocale();
  if (!countryCode) {
    return manualLocale
      ? { locale: manualLocale, source: "manual" }
      : { locale: getAutomaticLocale(), source: "browser" };
  }

  const automaticIpLocale = setAutomaticLocale(getCountryLocale(countryCode));

  if (manualLocale) {
    return { locale: manualLocale, source: "manual" };
  }

  await activateLocale(automaticIpLocale);
  return { locale: automaticIpLocale, source: "ip" };
}

export function useLocalizedMap<T extends Record<string, MessageDescriptor>>(map: T): Record<keyof T, string> {
  const { _ } = useLingui();

  return useMemo(() => mapValues(map, (value) => _(value)), [_, map]);
}
